import type { IngressoPedido } from '@/pages/pedido/types';
import type {
  CarteiraDoEvento,
  EventoCompras,
  IngressosDoEvento,
  ListasCompras,
} from './types';

/** Linha de `vw_meus_ingressos` (a view também traz o evento_id). */
export interface IngressoLinha extends IngressoPedido {
  evento_id?: number | null;
}

/** Linha de `minhas_carteiras` — só usamos as que têm fichas. */
export interface CarteiraDaConta {
  carteira_codigo: string;
  evento_id: number;
  evento_nome: string;
  evento_inicio: string | null;
  itens_loja: number;
  pendentes: number;
}

/** Carteira guardada só neste aparelho (localStorage "carteira:<evento>"). */
export interface CarteiraDoAparelho {
  codigo: string;
  eventoId: number;
  eventoNome: string;
  eventoInicio: string | null;
  fichas: number;
}

function chaveDoIngresso(ing: IngressoLinha): string {
  return ing.evento_id != null ? `e${ing.evento_id}` : `s${ing.evento_slug}`;
}

function resumoIngressos(lista: IngressoLinha[]): IngressosDoEvento {
  const pagos = lista.filter((i) => i.status_pedido === 'pago');
  const aguardando = lista.filter((i) => i.status_pedido === 'aguardando');
  const base = lista[0];

  return {
    eventoId: base.evento_id ?? null,
    evento: base.evento,
    eventoSlug: base.evento_slug,
    eventoEm: base.evento_em,
    pagos: pagos.length,
    aguardando: aguardando.length,
    pedidoPagar: aguardando[0]?.pedido_id ?? null,
    pedidoVer: pagos[0]?.pedido_id ?? null,
    statusPedido: (pagos[0]?.status_pedido ?? aguardando[0]?.status_pedido ?? null) as
      | IngressosDoEvento['statusPedido'],
  };
}

// Junta ingressos + carteiras da conta + carteiras do aparelho em um cartão por
// evento e separa em Próximos (mais perto primeiro) e Anteriores (mais recente
// primeiro). Carteira sem ficha não gera cartão.
export function montarListas(
  ingressos: IngressoLinha[],
  daConta: CarteiraDaConta[],
  doAparelho: CarteiraDoAparelho[],
): ListasCompras {
  const mapa = new Map<string, EventoCompras>();

  const pegar = (
    chave: string,
    eventoId: number | null,
    evento: string,
    eventoSlug: string | null,
    eventoEm: string | null,
  ): EventoCompras => {
    const existente = mapa.get(chave);
    if (existente) {
      if (!existente.eventoSlug && eventoSlug) existente.eventoSlug = eventoSlug;
      if (!existente.eventoEm && eventoEm) existente.eventoEm = eventoEm;
      return existente;
    }
    const novo: EventoCompras = {
      chave,
      eventoId,
      evento,
      eventoSlug,
      eventoEm,
      ingressos: null,
      carteira: null,
    };
    mapa.set(chave, novo);
    return novo;
  };

  const porEvento = new Map<string, IngressoLinha[]>();
  ingressos.forEach((ing) => {
    const chave = chaveDoIngresso(ing);
    const lista = porEvento.get(chave) ?? [];
    lista.push(ing);
    porEvento.set(chave, lista);
  });

  porEvento.forEach((lista, chave) => {
    const resumo = resumoIngressos(lista);
    const evento = pegar(
      chave,
      resumo.eventoId,
      resumo.evento,
      resumo.eventoSlug,
      resumo.eventoEm,
    );
    evento.ingressos = resumo;
  });

  daConta.forEach((c) => {
    if ((c.itens_loja ?? 0) <= 0) return;
    const evento = pegar(`e${c.evento_id}`, c.evento_id, c.evento_nome, null, c.evento_inicio);
    const carteira: CarteiraDoEvento = {
      codigo: c.carteira_codigo,
      fichas: c.itens_loja,
      pendentes: c.pendentes ?? 0,
    };
    evento.carteira = carteira;
  });

  doAparelho.forEach((c) => {
    if (c.fichas <= 0) return;
    const evento = pegar(`e${c.eventoId}`, c.eventoId, c.eventoNome, null, c.eventoInicio);
    if (!evento.carteira) {
      evento.carteira = { codigo: c.codigo, fichas: c.fichas, pendentes: 0 };
    }
  });

  const agora = Date.now();
  const quando = (e: EventoCompras) => (e.eventoEm ? new Date(e.eventoEm).getTime() : null);

  const todos = [...mapa.values()];

  const proximos = todos
    .filter((e) => {
      const q = quando(e);
      return q == null || q >= agora;
    })
    .sort((a, b) => {
      const qa = quando(a);
      const qb = quando(b);
      return (qa ?? Number.MAX_SAFE_INTEGER) - (qb ?? Number.MAX_SAFE_INTEGER);
    });

  const anteriores = todos
    .filter((e) => {
      const q = quando(e);
      return q != null && q < agora;
    })
    .sort((a, b) => (quando(b) ?? 0) - (quando(a) ?? 0));

  return { proximos, anteriores };
}